import { useEffect, useImperativeHandle, useRef, useState } from "react";
import {
  FRAME_FADE_MS,
  LENS_START,
  panView,
  prefersReducedMotion,
  zoomView,
} from "utils/viewerInput";

const KEY_ZOOM = { "+": 1.25, "=": 1.25, "-": 0.8, _: 0.8 };
const KEY_PAN = {
  ArrowLeft: [0.1, 0],
  ArrowRight: [-0.1, 0],
  ArrowUp: [0, 0.1],
  ArrowDown: [0, -0.1],
};

// One lens at its own resolution. Zoom comes from the page's wheel/pinch handler (through
// `viewRef`), + / − or a double-click; once zoomed, drag or Shift+arrows pan. The zoom is kept
// when the frame changes, so a detail can be followed through the captures, and each new frame
// fades in over the previous one.
export default function LensView({ src, alt, resetKey, viewRef, describedBy }) {
  const box = useRef(null);
  const drag = useRef(null);
  const nextLayer = useRef(1);
  const [view, setView] = useState(LENS_START);
  const [layers, setLayers] = useState(() =>
    src ? [{ id: 0, src, entering: false }] : [],
  );

  useEffect(() => {
    const id = nextLayer.current++;
    setLayers((old) => {
      const top = old[old.length - 1];
      if (!src) return old.length ? [] : old;
      if (top?.src === src) return old;
      // The previous frame stays underneath, fully opaque, until the new one has faded in.
      return top && !prefersReducedMotion()
        ? [{ ...top, entering: false }, { id, src, entering: true }]
        : [{ id, src, entering: false }];
    });
  }, [src]);

  useEffect(() => {
    if (layers.length < 2) return undefined;
    const timer = setTimeout(
      () => setLayers((old) => old.slice(-1).map((l) => ({ ...l, entering: false }))),
      FRAME_FADE_MS,
    );
    return () => clearTimeout(timer);
  }, [layers]);

  useEffect(() => {
    setView(LENS_START);
  }, [resetKey]);

  const pointAt = (clientX, clientY) => {
    const rect = box.current?.getBoundingClientRect();
    if (!rect?.width || !rect?.height || clientX === undefined)
      return { x: 0.5, y: 0.5 };
    return {
      x: (clientX - rect.left) / rect.width,
      y: (clientY - rect.top) / rect.height,
    };
  };
  useImperativeHandle(viewRef, () => ({
    zoomBy(factor, clientX, clientY) {
      const point = pointAt(clientX, clientY);
      setView((v) => zoomView(v, factor, point));
    },
  }));

  const top = layers[layers.length - 1];
  return (
    <div
      ref={box}
      className={`field-lens${view.scale > 1 ? " is-zoomed" : ""}`}
      tabIndex={0}
      role="group"
      aria-label={`${alt}. Scroll, pinch or press + and − to zoom; drag to pan when zoomed.`}
      aria-describedby={describedBy}
      data-scale={view.scale}
      onPointerDown={(e) => {
        if (view.scale <= 1) return;
        e.currentTarget.setPointerCapture?.(e.pointerId);
        const rect = box.current.getBoundingClientRect();
        drag.current = {
          x: e.clientX,
          y: e.clientY,
          view,
          width: rect.width || 1,
          height: rect.height || 1,
        };
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (d)
          setView(
            panView(d.view, (e.clientX - d.x) / d.width, (e.clientY - d.y) / d.height),
          );
      }}
      onPointerUp={() => {
        drag.current = null;
      }}
      onPointerCancel={() => {
        drag.current = null;
      }}
      onDoubleClick={(e) => {
        const point = pointAt(e.clientX, e.clientY);
        setView((v) => (v.scale > 1 ? LENS_START : zoomView(v, 2.5, point)));
      }}
      onKeyDown={(e) => {
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        const pan = KEY_PAN[e.key];
        // Plain Left/Right step captures (handled by the page); Shift+arrows and Up/Down pan.
        if (pan && (e.shiftKey || e.key === "ArrowUp" || e.key === "ArrowDown")) {
          e.preventDefault();
          setView((v) => panView(v, pan[0] / v.scale, pan[1] / v.scale));
        } else if (KEY_ZOOM[e.key]) {
          e.preventDefault();
          setView((v) => zoomView(v, KEY_ZOOM[e.key]));
        }
      }}
    >
      <div
        className="field-lens-content"
        style={{
          transform: `translate(${view.x * 100}%, ${view.y * 100}%) scale(${view.scale})`,
        }}
      >
        {layers.map((layer) => (
          <img
            key={layer.id}
            src={layer.src}
            alt={layer === top ? alt : ""}
            className={layer.entering ? "is-entering" : undefined}
            draggable={false}
          />
        ))}
      </div>
    </div>
  );
}
